import { useCallback, useEffect, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { MinimalDevice } from '@rtkwlf/asset-data-service';
import { useToastContext } from '@rtkwlf/fenrir-react';
import { faArrowDownToLine } from '@rtkwlf/pro-solid-svg-icons/faArrowDownToLine';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';

import { useGetAllAssets } from '@apis/asset-data-service/queries/use-get-all-assets';
import { Button, CircularProgress, Tooltip } from '@packages/fenrir/components';
import { useDownload } from '@packages/hooks/useDownload';
import { useOrganizationContext } from '@packages/legacy/organization-context';
import { TOAST_DURATION_SHORT } from 'ae-constants/toast';

import { useAssetsExportSchema } from './use-assets-export-schema/use-assets-export-schema';

export const AssetsExport = () => {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const { t } = useTranslation('ads', { keyPrefix: 'assetsTable.export' });
  const { getCSVFields, getCSVRowData } = useAssetsExportSchema();
  const { selectedOrganization } = useOrganizationContext();
  const { addToast } = useToastContext();

  const {
    data,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    error,
    hasNextPage,
    fetchNextPage,
  } = useGetAllAssets({ options: { enabled: isExporting } });

  const transformedAssetsData = useMemo(() => {
    if (!data) return;

    return data.reduce((acc, page) => {
      const { devices } = page;
      if (devices) {
        acc.push(...devices);
      }
      return acc;
    }, [] as MinimalDevice[]);
  }, [data]);

  const csvText = useMemo(() => {
    if (!transformedAssetsData?.length) return '';

    const csv = Papa.unparse({
      data: transformedAssetsData.map(getCSVRowData),
      fields: getCSVFields(),
    });

    return csv;
  }, [getCSVFields, getCSVRowData, transformedAssetsData]);

  const csvFileName = t('fileName', {
    orgName: selectedOrganization.name,
    date: dayjs().format('YYYY-MM-DD'),
  });

  const { download } = useDownload(csvFileName, csvText);

  const handleAssetsExportError = useCallback(() => {
    setIsExporting(false);
    addToast({
      appearance: 'danger',
      duration: TOAST_DURATION_SHORT,
      title: t('exportAssetsErrorTitle'),
      message: t('exportAssetsErrorMessage'),
    });
  }, [addToast, t]);

  const handleAssetsFetchComplete = useCallback(() => {
    try {
      download();
      addToast({
        appearance: 'success',
        duration: TOAST_DURATION_SHORT,
        title: t('exportAssetsSuccessTitle'),
        message: '',
      });
      setIsExporting(false);
    } catch {
      handleAssetsExportError();
    }
  }, [addToast, download, handleAssetsExportError, t]);

  const handleExportClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setIsExporting(true);
      refetch();
    },
    [refetch],
  );

  useEffect(() => {
    const shouldFetchAssets = isExporting && !isFetching && !isFetchingNextPage;
    if (!shouldFetchAssets) return;

    if (isError) handleAssetsExportError();
    else if (hasNextPage) fetchNextPage();
    else handleAssetsFetchComplete();
  }, [
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
    data,
    download,
    handleAssetsExportError,
    handleAssetsFetchComplete,
    isExporting,
  ]);

  return (
    <Tooltip title={t('exportAssetsDescription')}>
      <Button
        data-testid="export-assets-button"
        variant="text"
        color="primary"
        onClick={handleExportClick}
        disabled={isExporting}
        startIcon={
          isExporting ? (
            <CircularProgress size={15} color="inherit" />
          ) : (
            <FontAwesomeIcon icon={faArrowDownToLine} />
          )
        }
        size="small"
      >
        {isExporting ? t('exportingAssets') : t('exportAssets')}
      </Button>
    </Tooltip>
  );
};
